import type {
  FrontKind,
  Furniture,
  FurnitureModel,
  FurniturePart,
  Material,
  PlacedModule,
  Vec2,
} from "../types";
import { boundingBox, unionOutline } from "../geom";
import { presetForName, frontKindForName } from "../catalog";
import { getTemplate, resolveParams, type FurnitureTemplate } from "./templates";
import { placedModuleOutline, placedModuleParts } from "./modules";
import { rect } from "./geometry";
import { sortPartsForPaint } from "./shading";

/**
 * Turns whatever a `Furniture` (or catalog item) carries — a parametric
 * template, a modular sectional, baked freeform parts, a legacy normalized
 * `shape`, or nothing at all — into a concrete list of parts plus a true
 * footprint outline. Every renderer (Konva canvas, SVG previews) and every
 * geometry consumer (snap / clamp / AI wire) goes through this so they can
 * never disagree about what a piece looks like.
 *
 * Results are memoized on a structural key so dragging (which only changes
 * `x`/`y`) never re-tessellates.
 */

/** The subset of `Furniture` the resolver cares about. */
export type FurnitureLike = Pick<
  Furniture,
  "name" | "width" | "depth" | "height" | "color" | "shape" | "model" | "material" | "frontKind"
>;

export interface ResolvedFurniture {
  model: FurnitureModel;
  /** Parts sorted bottom-to-top for painting. */
  parts: FurniturePart[];
  /** True footprint outline, footprint-local inches (origin at center). */
  footprint: Vec2[];
  material: Material;
  frontKind: FrontKind;
  /** Tallest part top, inches. */
  maxZ: number;
  /** The template when `model.kind === "parametric"`. */
  template?: FurnitureTemplate;
}

const cache = new Map<string, ResolvedFurniture>();
const CACHE_CAP = 400;

function cacheKey(f: FurnitureLike): string {
  // When a material is set, its tint is the color that gets painted. The
  // legacy `color` field would otherwise bust the cache on every material
  // click (the inspector updates both), even for swatches that override
  // the material and only inherit the piece's new color.
  return JSON.stringify([
    f.name,
    f.width,
    f.depth,
    f.height,
    f.material ? null : f.color,
    f.model ?? null,
    f.material ?? null,
    f.frontKind ?? null,
    f.model ? null : (f.shape ?? null),
  ]);
}

/** Resolve the effective model for a piece without building parts. */
export function resolveModel(f: FurnitureLike): FurnitureModel {
  if (f.model) return f.model;
  if (f.shape && f.shape.length >= 3) {
    // Legacy creator piece: a single body from the normalized outline.
    const outline = f.shape.map((p) => ({ x: p.x * f.width, y: p.y * f.depth }));
    return {
      kind: "freeform",
      parts: [{ id: "body", role: "body", outline, z0: 0, z1: Math.max(1, f.height) }],
    };
  }
  const preset = presetForName(f.name);
  if (preset) {
    return { kind: "parametric", template: preset.template, params: { ...(preset.params ?? {}) } };
  }
  return { kind: "parametric", template: "box", params: {} };
}

export function resolveMaterial(f: FurnitureLike): Material {
  if (f.material) return f.material;
  const model = f.model ?? resolveModel(f);
  if (model.kind === "parametric") {
    return { id: getTemplate(model.template).defaultMaterial, tint: f.color };
  }
  if (model.kind === "modular") return { id: "fabric", tint: f.color };
  return { id: "matte", tint: f.color };
}

export function resolveFrontKind(f: FurnitureLike): FrontKind {
  if (f.frontKind) return f.frontKind;
  const model = f.model ?? resolveModel(f);
  if (model.kind === "parametric") {
    const tpl = getTemplate(model.template);
    if (model.template !== "box") return tpl.frontKind;
  }
  if (model.kind === "modular") return "seat";
  return frontKindForName(f.name);
}

export function resolveFurniture(f: FurnitureLike): ResolvedFurniture {
  const key = cacheKey(f);
  const hit = cache.get(key);
  if (hit) return hit;
  const result = build(f);
  if (cache.size >= CACHE_CAP) {
    // Drop the oldest entry (Map preserves insertion order).
    const first = cache.keys().next().value;
    if (first !== undefined) cache.delete(first);
  }
  cache.set(key, result);
  return result;
}

/** Test hook. */
export function clearResolveCache() {
  cache.clear();
}

function build(f: FurnitureLike): ResolvedFurniture {
  const model = resolveModel(f);
  const material = resolveMaterial({ ...f, model });
  const frontKind = resolveFrontKind({ ...f, model });
  const w = Math.max(1, f.width);
  const d = Math.max(1, f.depth);
  const h = Math.max(0.5, f.height);

  let parts: FurniturePart[];
  let footprint: Vec2[] | undefined;
  let template: FurnitureTemplate | undefined;

  if (model.kind === "parametric") {
    template = getTemplate(model.template);
    const params = resolveParams(template, model.params);
    const built = template.build({ w, d, h, params });
    parts = built.parts;
    footprint = built.footprint;
  } else if (model.kind === "modular") {
    const centered = centerModules(model.modules);
    parts = centered.modules.flatMap((m) => placedModuleParts(m, h));
    footprint = unionOutline(centered.modules.map(placedModuleOutline)) ?? undefined;
    // Modular pieces are authored at real size; if width/depth drifted from
    // the union bbox (e.g. inspector edit), scale parts to fit.
    const bb = boundingBox(footprint ?? parts.flatMap((p) => p.outline));
    if (bb.width > 0 && bb.height > 0 && (Math.abs(bb.width - w) > 1e-6 || Math.abs(bb.height - d) > 1e-6)) {
      const sx = w / bb.width;
      const sy = d / bb.height;
      parts = parts.map((p) => scalePart(p, sx, sy));
      footprint = footprint?.map((p) => ({ x: p.x * sx, y: p.y * sy }));
    }
  } else {
    // Freeform: baked parts authored at some nominal size — scale them into
    // the current footprint so resizing the piece rescales the outline.
    const all = model.parts.flatMap((p) => p.outline);
    const bb = boundingBox(all);
    const sx = bb.width > 0 ? w / bb.width : 1;
    const sy = bb.height > 0 ? d / bb.height : 1;
    const cx = bb.minX + bb.width / 2;
    const cy = bb.minY + bb.height / 2;
    parts = model.parts.map((p) => scalePart(translatePart(p, -cx, -cy), sx, sy));
    const bodies = parts.filter((p) => p.role === "body");
    footprint =
      (bodies.length ? unionOutline(bodies.map((p) => p.outline)) : null) ??
      unionOutline(parts.map((p) => p.outline)) ??
      undefined;
  }

  if (!footprint || footprint.length < 3) {
    footprint = rect(-w / 2, -d / 2, w, d);
  }
  if (parts.length === 0) {
    parts = [{ id: "body", role: "body", outline: footprint, z0: 0, z1: h }];
  }
  const maxZ = parts.reduce((m, p) => Math.max(m, p.z1), 0);
  return {
    model,
    parts: sortPartsForPaint(parts),
    footprint,
    material,
    frontKind,
    maxZ,
    template,
  };
}

function translatePart(p: FurniturePart, dx: number, dy: number): FurniturePart {
  return {
    ...p,
    outline: p.outline.map((q) => ({ x: q.x + dx, y: q.y + dy })),
    seams: p.seams?.map((s) => ({
      a: { x: s.a.x + dx, y: s.a.y + dy },
      b: { x: s.b.x + dx, y: s.b.y + dy },
    })),
  };
}

function scalePart(p: FurniturePart, sx: number, sy: number): FurniturePart {
  if (sx === 1 && sy === 1) return p;
  return {
    ...p,
    outline: p.outline.map((q) => ({ x: q.x * sx, y: q.y * sy })),
    seams: p.seams?.map((s) => ({
      a: { x: s.a.x * sx, y: s.a.y * sy },
      b: { x: s.b.x * sx, y: s.b.y * sy },
    })),
    cornerRadius: p.cornerRadius != null ? p.cornerRadius * Math.min(sx, sy) : undefined,
  };
}

/**
 * Recenter a set of modules so the union's bounding box is centered on the
 * origin. Returns the shifted modules plus the bbox size — callers editing a
 * modular piece use `width`/`depth` to keep `Furniture.width/depth` in sync.
 */
export function centerModules(modules: PlacedModule[]): {
  modules: PlacedModule[];
  width: number;
  depth: number;
  dx: number;
  dy: number;
} {
  if (modules.length === 0) return { modules, width: 30, depth: 30, dx: 0, dy: 0 };
  const all = modules.flatMap(placedModuleOutline);
  const bb = boundingBox(all);
  const cx = bb.minX + bb.width / 2;
  const cy = bb.minY + bb.height / 2;
  if (Math.abs(cx) < 1e-6 && Math.abs(cy) < 1e-6) {
    return { modules, width: bb.width, depth: bb.height, dx: 0, dy: 0 };
  }
  return {
    modules: modules.map((m) => ({ ...m, x: m.x - cx, y: m.y - cy })),
    width: bb.width,
    depth: bb.height,
    dx: -cx,
    dy: -cy,
  };
}

/**
 * Upgrade a legacy `Furniture` (no `model`) to carry an explicit model,
 * material, and frontKind. Idempotent. Used by the persist migration and
 * the library / shared-room load paths so old designs render with the
 * parametric system instead of flat boxes.
 */
export function normalizeFurniture<T extends FurnitureLike>(f: T): T {
  if (f.model && f.material && f.frontKind) return f;
  const model = f.model ?? resolveModel(f);
  const material = f.material ?? resolveMaterial({ ...f, model });
  const frontKind = f.frontKind ?? resolveFrontKind({ ...f, model });
  return { ...f, model, material, frontKind };
}
